import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Loader, Eye, EyeOff } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { useGoogleOAuth } from '../../hooks/useGoogleOAuth';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import AuthSplitLayout from './AuthSplitLayout';
import AuthInstallWidget from './AuthInstallWidget';
import './SimpleRegister.css'; // shared form styling (.srx-* classes)
import './SimpleLogin.css';     // login-specific additions (.slx-* classes)

/**
 * Mobile-first merchant login page.
 * - Email + password form
 * - "Forgot password?" link
 * - Google sign-in
 * - On 200 with `requiresOTP: true` → navigates to /verify-otp?email=...&purpose=login
 * - On 200 with `token` (trust-cookie path or mobile bypass) → SecureAuthContext refresh, redirect to /onboarding or /dashboard based on user state
 */
const SimpleLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useSecureAuth();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Google OAuth (signin mode)
  const { handleGoogleAuth, loading: googleLoading, error: googleError } = useGoogleOAuth();
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      await handleGoogleAuth(tokenResponse.access_token, 'signin');
    },
    onError: () => {
      setError('Google sign-in failed. Please try again or use your email and password.');
    }
  });

  const canSubmit =
    formData.email.trim().length > 0 &&
    formData.password.length > 0 &&
    !loading;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/merchant/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          password: formData.password
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.reason || data.error || 'Login failed. Please try again.');
        return;
      }

      // OTP path — backend sent a login code, navigate to /verify-otp with purpose=login
      if (data.requiresOTP) {
        const emailParam = encodeURIComponent(formData.email.trim().toLowerCase());
        const redirectAfter = searchParams.get('redirect');
        const qs = `email=${emailParam}&purpose=login${redirectAfter ? `&redirect=${encodeURIComponent(redirectAfter)}` : ''}`;
        navigate(`/verify-otp?${qs}`);
        return;
      }

      // Trust-cookie / mobile-bypass path — backend issued JWT directly
      if (data.success && data.token) {
        await refreshUser();
        const redirectAfter = searchParams.get('redirect');
        if (redirectAfter && redirectAfter.startsWith('/')) {
          navigate(decodeURIComponent(redirectAfter), { replace: true });
        } else {
          // Default: backend doesn't tell us onboarding state in this response;
          // navigate to /overview (canonical post-login landing) and let route
          // guards redirect to /onboarding if needed
          navigate('/overview', { replace: true });
        }
        return;
      }

      setError(data.error || 'Unexpected response from server.');
    } catch (err) {
      console.error('[SimpleLogin] submit error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitLayout>
      <div className="srx-container">
        <div className="srx-content">
          <div className="srx-header">
            <img src="/logo192.png" alt="Revstar" className="srx-logo" />
            <h1 className="srx-title">Welcome back</h1>
            <p className="srx-subtitle">Sign in to manage your store.</p>
          </div>

          <form className="srx-form" onSubmit={handleSubmit} noValidate>
            {(error || googleError) && (
              <div className="srx-error" role="alert" aria-live="polite">
                {error || googleError}
              </div>
            )}

            <div className="srx-field">
              <label htmlFor="login-email" className="srx-label">Email</label>
              <div className="srx-input-wrap">
                <span className="srx-input-icon">
                  <Mail size={18} aria-hidden="true" />
                </span>
                <input
                  id="login-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  className="srx-input"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="srx-field">
              <label htmlFor="login-password" className="srx-label">Password</label>
              <div className="srx-input-wrap">
                <span className="srx-input-icon">
                  <Lock size={18} aria-hidden="true" />
                </span>
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="srx-input srx-input-password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="srx-eye"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div className="slx-forgot-row">
                <Link to="/forgot-password" className="slx-forgot-link">Forgot password?</Link>
              </div>
            </div>

            <button type="submit" className="srx-cta" disabled={!canSubmit}>
              {loading ? (
                <>
                  <Loader size={18} className="srx-spin" aria-hidden="true" />
                  <span>Signing in...</span>
                </>
              ) : (
                <span>Sign in</span>
              )}
            </button>

            <div className="srx-divider">
              <span>or</span>
            </div>

            <button
              type="button"
              className="srx-google"
              onClick={() => googleLogin()}
              disabled={loading || googleLoading}
            >
              {googleLoading ? (
                <>
                  <Loader size={18} className="srx-spin" aria-hidden="true" />
                  <span>Signing in with Google...</span>
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span>Continue with Google</span>
                </>
              )}
            </button>

            <p className="srx-footer">
              Don't have an account?{' '}
              <Link to="/signup" className="srx-footer-link">
                Sign up
              </Link>
            </p>
          </form>
        </div>
      </div>
      <AuthInstallWidget />
    </AuthSplitLayout>
  );
};

export default SimpleLogin;
